import { mainApi } from '../../../../api/mainApi';
import { AccessState } from '../../../../types';
import { middlewareApi } from '../../../middleware/middlewareApi';
import { setCoursesTeacherList } from '../../../teachers/courses/slices/coursesTeacherSlice';
import { ClassroomEdLvlByPeriodResponse } from '../../classrooms-educational-levels/interfaces/classroomEdLvl.interface';
import { StudentCourse } from '../interfaces/course-students.interfaces';
import {
  Course,
  CourseByFilter,
  CoursePayload,
  CourseResponse,
  CoursesByFilterResponse,
  Faculty,
  Parallel,
} from '../interfaces/course.interface';
import { CoursesCollaboratorResponse } from '../interfaces/coursesCollaborator.interface';

export const courseTags = middlewareApi.enhanceEndpoints({
  addTagTypes: [
    'Course',
    'CoursesCollaborator',
    'ClassroomsSelected',
    'VirtualCourses',
    'Parallel',
  ],
});
// Define a service using a base URL and expected endpoints
export const courseApi = courseTags.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * @GET all courses by period
     */
    getCourses: builder.query<Course[], { profileId?: number; periodId: number }>({
      queryFn: async ({ profileId, periodId }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = profileId || currentProfile?.profileId;

          const { data } = await mainApi.get<Course[]>(`/periods/${periodId}/courses`, {
            params: {
              user_profile_id,
            },
          });

          return { data: Array.isArray(data) ? data : [] };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'Course', id } as const)),
              { type: 'Course', id: 'LIST' },
            ]
          : [{ type: 'Course', id: 'LIST' }],
    }),

    /**
     * @GET all courses by filter
     */
    getCoursesByFilters: builder.query<
      CoursesByFilterResponse,
      {
        profileId: number;
        periodId?: number;
        educationLevelId?: number;
        offerDescription?: string;
        campusId: number;
        modalityId?: number;
        hourhandId?: number;
        parallelId?: number;
        search?: string;
        page?: number;
        perPage?: number;
        sort?: string;
        type_sort?: string;
      }
    >({
      queryFn: async ({
        profileId,
        periodId,
        educationLevelId,
        offerDescription,
        campusId,
        modalityId,
        hourhandId,
        parallelId,
        search,
        page,
        perPage,
        sort,
        type_sort,
      }) => {
        try {
          const { data } = await mainApi.get<CoursesByFilterResponse>(
            `/periods/${periodId}/education-levels/${educationLevelId}/courses`,
            {
              params: {
                user_profile_id: profileId,
                off_description: offerDescription,
                campus_id: campusId,
                modality_id: modalityId,
                hourhand_id: hourhandId,
                parallel_id: parallelId,
                search,
                page,
                size: perPage,
                ...(type_sort && { type_sort }),
                ...(sort && { sort }),
              },
            }
          );

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result, error, { profileId }) => [{ type: 'Course', id: 'TABLE', profileId }],
    }),

    /**
     * @GET all courses by filter with data all
     */
    getCoursesByFiltersAll: builder.query<
      CourseByFilter[],
      {
        periodId?: number;
        educationLevelId?: number;
        offerDescription?: string;
        campusId: number;
        modalityId?: number;
        hourhandId?: number;
        parallelId?: number;
        search?: string;
        sort?: string;
        type_sort?: string;
      }
    >({
      queryFn: async (
        {
          periodId,
          educationLevelId,
          offerDescription,
          campusId,
          modalityId,
          hourhandId,
          parallelId,
          search,
          sort,
          type_sort,
        },
        { getState }
      ) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = currentProfile?.profileId;

          const { data } = await mainApi.get<CourseByFilter[]>(
            `/periods/${periodId}/education-levels/${educationLevelId}/courses`,
            {
              params: {
                user_profile_id,
                off_description: offerDescription,
                campus_id: campusId,
                modality_id: modalityId,
                hourhand_id: hourhandId,
                parallel_id: parallelId,
                ...(type_sort && { type_sort }),
                ...(sort && { sort }),
                search,
                data: 'all',
              },
            }
          );

          return { data: Array.isArray(data) ? data : [] };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result, error) => [{ type: 'Course', id: 'TABLE' }],
      // providesTags: (result, error, { educationLevelId }) => [
      //   { type: 'Course', id: 'TABLE', educationLevelId },
      // ],
    }),

    /**
     * @GET Course by id
     */
    getCourse: builder.query<Course, { profileId: number; courseId: number }>({
      queryFn: async ({ profileId, courseId }) => {
        try {
          const { data } = await mainApi.get<Course>(`/courses/${courseId}`, {
            params: {
              user_profile_id: profileId,
            },
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result, error) => [{ type: 'Course', id: 'TABLE' }],
    }),

    /**
     * @GET Courses by period
     */
    getPeriodCourses: builder.query<Course[], { profileId?: number; periodId: number }>({
      queryFn: async ({ profileId, periodId }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = profileId || currentProfile?.profileId;

          const { data } = await mainApi.get<Course[]>(`/periods/${periodId}/courses`, {
            params: {
              user_profile_id,
            },
          });

          return { data: Array.isArray(data) ? data : [] };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result, error) => [{ type: 'Course', id: 'TABLE' }],
    }),

    /**
     * @GET Courses by classrooms
     */
    getClassroomCourses: builder.query<
      CourseResponse,
      {
        profileId: number;
        classroomId: number;
        perPage?: number;
        page?: number;
        type_sort?: string;
        sort?: string;
        search?: string;
        periodId?: number;
      }
    >({
      queryFn: async ({
        profileId,
        classroomId,
        perPage,
        page,
        type_sort,
        sort,
        search,
        periodId,
      }) => {
        try {
          const { data } = await mainApi.get<CourseResponse>(`/classrooms/${classroomId}/courses`, {
            params: {
              user_profile_id: profileId,
              ...(perPage && { size: perPage }),
              ...(page && { page }),
              ...(type_sort && { type_sort }),
              ...(sort && { sort }),
              ...(search && { search }),
              ...(periodId && { period_id: periodId }),
            },
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result, error, { profileId }) => [
        { type: 'Course', profileId },
        { type: 'Course', id: 'TABLE' },
      ],
    }),

    /**
     * @GET obtiene cursos del colaborador por periodo
     */
    getCoursesCollaborator: builder.query<
      CoursesCollaboratorResponse,
      {
        collId: number;
        periodId: number;
        size?: number;
        page?: number;
        type_sort?: string;
        search?: string;
      }
    >({
      queryFn: async (
        { collId, periodId, size, page, type_sort, search },
        { getState, dispatch }
      ) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = currentProfile?.profileId;

          const { data } = await mainApi.get<CoursesCollaboratorResponse>(
            `/collaborators/${collId}/period/${periodId}/course`,
            {
              params: {
                user_profile_id,
                size,
                page,
                type_sort,
                search,
              },
            }
          );

          dispatch(setCoursesTeacherList(data?.data));

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ id }) => ({ type: 'CoursesCollaborator', id } as const)),
              { type: 'CoursesCollaborator', id: 'LIST' },
            ]
          : [{ type: 'CoursesCollaborator', id: 'LIST' }],
    }),

    /**
     * @GET obtain all students course
     */
    getAllStudentsCourse: builder.query<StudentCourse[], { courseId: number }>({
      queryFn: async ({ courseId }, { getState, dispatch }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = currentProfile?.profileId;

          const { data } = await mainApi.get<StudentCourse[]>(`/course/${courseId}/students`, {
            params: {
              user_profile_id,
              sort: 'pers_full_lastname',
              type_sort: 'asc',
              data: 'all',
            },
          });

          return { data: Array.isArray(data) ? data : [] };
        } catch (error: any) {
          return { error };
        }
      },
    }),

    /**
     * @POST create course
     */
    addCourse: builder.mutation<
      Course,
      { profileId: number; coursePayload: Partial<CoursePayload> }
    >({
      queryFn: async ({ profileId, coursePayload }) => {
        try {
          const { data } = await mainApi.post<Course>('/courses', {
            user_profile_id: profileId,
            ...coursePayload,
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [
        { type: 'Course', id: 'LIST' },
        { type: 'Course', id: 'TABLE' },
        { type: 'CoursesCollaborator', id: 'LIST' },
        { type: 'VirtualCourses', id: 'Virtuals' },
        { type: 'ClassroomsSelected', id: 'CL_LIST' },
        { type: 'Parallel', id: 'LIST' },
      ],
    }),

    /**
     * @POST create course
     */
    assignCoursesMoodle: builder.mutation<
      { courses: number[] },
      { coursesMoodlePayload: { courses: number[] }; periodId: number }
    >({
      queryFn: async ({ coursesMoodlePayload, periodId }, { getState }) => {
        try {
          const {
            access: {
              currentProfile: { profileId },
            },
          } = getState() as { access: { currentProfile: { profileId: number } } };

          const { data } = await mainApi.post<{ courses: number[] }>(
            `courses/periods/${periodId}/moodle/migrate`,
            {
              user_profile_id: profileId,
              ...coursesMoodlePayload,
            }
          );

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [
        { type: 'Course', id: 'LIST' },
        { type: 'Course', id: 'TABLE' },
        { type: 'CoursesCollaborator', id: 'LIST' },
        { type: 'VirtualCourses', id: 'Virtuals' },
        { type: 'Parallel', id: 'LIST' },
        { type: 'ClassroomsSelected', id: 'CL_LIST' },
      ],
    }),

    /**
     * @PUT update course
     */
    updateCourse: builder.mutation<
      Course,
      {
        profileId: number;
        courseId: number;
        coursePayload: Partial<CoursePayload>;
      }
    >({
      queryFn: async ({ profileId, courseId, coursePayload }) => {
        try {
          const { data } = await mainApi.put<Course>(`/courses/${courseId}`, {
            user_profile_id: profileId,
            ...coursePayload,
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [
        { type: 'Course', id: 'LIST' },
        { type: 'Course', id: 'TABLE' },
        { type: 'CoursesCollaborator', id: 'LIST' },
        { type: 'VirtualCourses', id: 'Virtuals' },
        { type: 'Parallel', id: 'LIST' },
        { type: 'ClassroomsSelected', id: 'CL_LIST' },
      ],
    }),

    /**
     * @DELETE remove Course
     */
    deleteCourse: builder.mutation<Course, { profileId: number; courseId: number }>({
      queryFn: async ({ profileId, courseId }) => {
        try {
          const { data } = await mainApi.delete(`courses/${courseId}`, {
            data: {
              user_profile_id: profileId,
            },
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [
        { type: 'Course', id: 'LIST' },
        { type: 'Course', id: 'TABLE' },
        { type: 'CoursesCollaborator', id: 'LIST' },
        { type: 'VirtualCourses', id: 'Virtuals' },
        { type: 'Parallel', id: 'LIST' },
        { type: 'ClassroomsSelected', id: 'CL_LIST' },
      ],
    }),

    /**
     * @GET all selected classrooms by periods id and education level
     */
    getVirtualClassrooms: builder.query<
      ClassroomEdLvlByPeriodResponse,
      {
        profileId: number;
        page: number;
        perPage: number;
        search?: string;
        sort: string;
        order: string;
      }
    >({
      queryFn: async ({ profileId, page, search, perPage, sort, order }) => {
        try {
          const { data } = await mainApi.get<ClassroomEdLvlByPeriodResponse>(
            `/classrooms/virtuals`,
            {
              params: {
                user_profile_id: profileId,
                page,
                size: perPage,
                sort: sort,
                type_sort: order,
                search,
              },
            }
          );

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result) => [
        { type: 'VirtualCourses', id: 'Virtuals' },
        { type: 'Course', id: 'LIST' },
      ],
    }),

    /**
     * @POST create course
     */
    moodleMigration: builder.mutation<any, { courses: number[] }>({
      queryFn: async ({ courses }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = currentProfile?.profileId;

          const { data } = await mainApi.post<Course>('/courses', {
            user_profile_id,
            courses,
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [
        { type: 'Course', id: 'LIST' },
        { type: 'Course', id: 'TABLE' },
        { type: 'CoursesCollaborator', id: 'LIST' },
        { type: 'VirtualCourses', id: 'Virtuals' },
        { type: 'Parallel', id: 'LIST' },
        { type: 'ClassroomsSelected', id: 'CL_LIST' },
      ],
    }),

    getCurrentAvailableParallel: builder.query<
      Parallel[],
      {
        profileId: number;
        periodId: number;
        classroomId?: number;
        eduLevelId?: number;
        subjectId?: number | string;
      }
    >({
      queryFn: async ({ profileId, periodId, classroomId, eduLevelId, subjectId }) => {
        try {
          const { data } = await mainApi.get<Parallel>(`/parallels/available/new-parallel`, {
            params: {
              user_profile_id: profileId,
              period_id: periodId,
              ...(classroomId && { classroom_id: classroomId }),
              ...(eduLevelId && { education_level_id: eduLevelId }),
              ...(subjectId && { subject_id: subjectId }),
            },
          });

          return { data: [data] };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: [{ type: 'Parallel', id: 'LIST' }],
    }),

    getCareerByEduLevel: builder.query<Faculty, { profileId: number; eduLevelId: number }>({
      queryFn: async ({ profileId, eduLevelId }) => {
        try {
          const { data } = await mainApi.get<Faculty[]>(`/education-levels/parents`, {
            params: {
              user_profile_id: profileId,
              faculty_id: eduLevelId,
            },
          });

          return { data: data[0] };
        } catch (error: any) {
          return { error };
        }
      },
    }),

    ////
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetCoursesQuery,
  useGetCourseQuery,
  useGetCoursesByFiltersQuery,
  useLazyGetCoursesByFiltersQuery,
  useAddCourseMutation,
  useGetAllStudentsCourseQuery,
  useDeleteCourseMutation,
  useUpdateCourseMutation,
  useGetCoursesCollaboratorQuery,
  useGetVirtualClassroomsQuery,
  useGetClassroomCoursesQuery,
  useGetCurrentAvailableParallelQuery,
  useAssignCoursesMoodleMutation,
  useMoodleMigrationMutation,
  useLazyGetCoursesByFiltersAllQuery,
  useGetCareerByEduLevelQuery,
  useGetPeriodCoursesQuery,
} = courseApi;
